import type {
  ReactNode,
} from 'react'

import type {
  CalendarDate,
} from '@internationalized/date'

import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarHeading,
  DateInput,
  DatePicker as AriaDatePicker,
  DateSegment,
  Dialog,
  FieldError as AriaFieldError,
  Group,
  Label as AriaLabel,
  Popover,
  Text,
  type DatePickerProps as AriaDatePickerProps,
} from 'react-aria-components'

import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import { Button } from './button'

import type {
  FormFieldErrorMessage,
} from './form-field'

import { cn } from '@/shared/lib/cn'

export interface DatePickerProps
  extends Omit<
    AriaDatePickerProps<CalendarDate>,
    'children' | 'className'
  > {
  label: ReactNode
  description?: ReactNode
  errorMessage?: FormFieldErrorMessage

  firstDayOfWeek?:
    | 'sun'
    | 'mon'
    | 'tue'
    | 'wed'
    | 'thu'
    | 'fri'
    | 'sat'

  className?: string
}

export function DatePicker({
  label,
  description,
  errorMessage,
  firstDayOfWeek,
  isRequired = false,
  isInvalid,
  validationBehavior = 'aria',
  className,
  ...props
}: DatePickerProps) {
  const hasStaticError =
    typeof errorMessage === 'string' &&
    errorMessage.trim().length > 0

  return (
    <AriaDatePicker
      {...props}
      isRequired={isRequired}
      isInvalid={
        isInvalid ?? hasStaticError
      }
      validationBehavior={validationBehavior}
      className={cn(
        'flex w-full flex-col gap-1.5',
        className,
      )}
    >
      <AriaLabel className="flex items-center gap-1 text-sm font-medium">
        {label}

        {isRequired ? (
          <span
            aria-hidden="true"
            className="text-(--color-danger)"
          >
            *
          </span>
        ) : null}
      </AriaLabel>

      <Group className="flex min-h-10 items-center rounded-lg border border-(--color-border) bg-(--color-surface) px-3 transition-[border-color,box-shadow] 
      data-focus-within:border-(--color-primary) data-focus-within:ring-2 data-focus-within:ring-(--color-primary)/20 data-invalid:border-(--color-danger) data-disabled:opacity-60">
        <DateInput className="flex min-w-0 flex-1 items-center">
          {(segment) => (
            <DateSegment
              segment={segment}
              className={[
                'rounded px-0.5',
                'text-sm outline-none',
                'text-(--color-foreground)',
                'data-placeholder:text-(--color-muted-foreground)',
                'data-focused:bg-(--color-primary)',
                'data-focused:text-white',
                'data-disabled:opacity-50',
              ].join(' ')}
            />
          )}
        </DateInput>

        <Button
          slot="trigger"
          variant="ghost"
          size="small"
          className="h-8 w-8 shrink-0 p-0"
          aria-label="Open calendar"
        >
          <CalendarDays
            aria-hidden="true"
            className="h-4 w-4"
          />
        </Button>
      </Group>

      {description ? (
        <Text
          slot="description"
          className="text-xs leading-5 text-(--color-muted-foreground)"
        >
          {description}
        </Text>
      ) : null}

      <AriaFieldError className="text-xs leading-5 text-(--color-danger)">
        {errorMessage}
      </AriaFieldError>

      <Popover
        placement="bottom start"
        offset={6}
        className="z-50 rounded-xl border border-(--color-border) bg-(--color-surface) p-3 shadow-[var(--shadow-popover)] outline-none"
      >
        <Dialog className="outline-none">
          <Calendar
            firstDayOfWeek={
              firstDayOfWeek
            }
            className="w-76"
          >
            <header className="mb-3 flex items-center justify-between">
              <Button
                slot="previous"
                variant="ghost"
                size="small"
                className="h-9 w-9 p-0"
                aria-label="Previous month"
              >
                <ChevronLeft
                  aria-hidden="true"
                  className="h-4 w-4"
                />
              </Button>

              <CalendarHeading className="text-sm font-semibold" />

              <Button
                slot="next"
                variant="ghost"
                size="small"
                className="h-9 w-9 p-0"
                aria-label="Next month"
              >
                <ChevronRight
                  aria-hidden="true"
                  className="h-4 w-4"
                />
              </Button>
            </header>

            <CalendarGrid className="w-full border-separate border-spacing-1">
              {(date) => (
                <CalendarCell
                  date={date}
                  className={[
                    'flex h-9 w-9',
                    'cursor-pointer items-center',
                    'justify-center rounded-md',
                    'text-sm outline-none',
                    'transition-colors',
                    'hover:bg-(--color-surface-muted)',
                    'data-selected:bg-(--color-primary)',
                    'data-selected:text-white',
                    'data-today:font-bold',
                    'data-focus-visible:ring-2',
                    'data-focus-visible:ring-(--color-primary)',
                    'data-outside-month:text-(--color-muted-foreground)',
                    'data-outside-month:opacity-40',
                    'data-disabled:cursor-not-allowed',
                    'data-disabled:opacity-30',
                    'data-unavailable:line-through',
                  ].join(' ')}
                />
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </AriaDatePicker>
  )
}
